/* eslint-disable @typescript-eslint/no-explicit-any */
import { WorkoutExercise, WorkoutTiny } from 'generated/graphql';
import { LegacyWorkoutFormValues } from './workoutEdit.model';
import uuid4 from 'uuid4';
import { omit } from 'lodash';
import { Exercise as ExerciseEnum } from '@playbook-technologies/common-enums';
import {
  BuilderType,
  Circuit,
  LegacyExercise,
  LegacyHIIT,
  LegacyTabata,
  Media,
  Running,
  RunningExerciseType,
  RunningTempo,
  Template,
  Video,
  WorkoutItem,
} from './workout.domain';
import {
  isRunningExercise,
  runningTemplateToExercise,
  toRunningTemplateData,
} from '../view-models/mappers';
import { omitObject } from '@client/new/helpers/general';
import type {
  ExerciseSwapConfig,
  SwapSet,
} from '@client/new/features/exercise-swaps';

// Swap state is a server-mapped pass-through: creator-api attaches
// per-exercise swapConfig on read (slots matched, defaults pre-filled) and
// canonicalizes the write (dedupe, defaults, caps, snapshots) - the client
// only unwraps the proposal envelope on load and echoes form state on save.

export const getPayloadFromValues = ({
  id,
  influencerId,
  values,
  isClass,
}: {
  influencerId: number;
  id?: number;
  values: LegacyWorkoutFormValues;
  isClass: boolean;
}): Omit<Partial<WorkoutTiny>, 'swapConfig'> & {
  swapsEnabled?: boolean;
} => {
  const requiredEquipment = values.requiredEquipment
    ? values.requiredEquipment.split(',')
    : [];

  const exercises = values.exercises.map((ex: any) =>
    mapExerciseToPayload(ex, values, isClass),
  );

  const swapsEnabled =
    isClass || !values.programId ? undefined : values.swapsEnabled;

  return {
    id,
    influencerId,
    ...(swapsEnabled === undefined ? {} : { swapsEnabled }),
    name: values.name,
    description: values.description || '',
    isProtected: !values.freeSample,
    exercises,
    duration: Number(values.duration),
    messagePush: values.messagePush,
    programId: values.programId,
    requiredEquipment,
    media: values?.introVideo || null,
    mediaCover: values?.thumbnail || null,
    timeZone: values.timeZone,
  };
};

export const workoutFormData = (
  data: WorkoutTiny,
): LegacyWorkoutFormValues => ({
  name: data.name || '',
  description: data.description || '',
  tags: data.tags || [],
  duration: data.duration || 0,
  id: (data as any).id,
  messagePush: data.messagePush || undefined,
  introVideo: (data.media as Media) || undefined,
  thumbnail: (data.mediaCover as Media) || undefined,
  programId: data.programId || undefined,
  freeSample: !data.isProtected,
  requiredEquipment: data.requiredEquipment
    ? data.requiredEquipment.join(', ')
    : undefined,
  exercises: mapExercises(
    unwrapSwapConfigs((data?.exercises as WorkoutExercise[]) || []),
  ),
  swapsEnabled: data.swapConfig?.swapsEnabled,
  mediaExercise: (data.mediaExercise as Media) || undefined,
  publishedAt: data?.publishedAt || undefined,
  timeZone: data?.timeZone || undefined,
});

const mapExercises = (exercises?: any) => {
  return exercises?.map((e: any) => {
    const isRunning = isRunningExercise(e);
    if (!isRunning) return { ...e, uuid: uuid4() };

    return toRunningTemplateData(e);
  });
};

type GqlExerciseSwapConfig = NonNullable<
  Extract<
    WorkoutExercise,
    { __typename?: 'WorkoutExerciseSingle' }
  >['swapConfig']
>;

const toProposalSets = (
  sets: GqlExerciseSwapConfig['proposals'][number]['exercise']['sets'],
): SwapSet[] =>
  (sets ?? []).flatMap((set) =>
    set
      ? [
          {
            reps: (set.reps ?? []).filter((rep): rep is string => rep !== null),
          },
        ]
      : [],
  );

// Proposals arrive enveloped ({exercise}) from the read; the form keeps them
// naked - the save echoes the naked shape, which the backend accepts too.
const toFormSwapConfig = (
  config: GqlExerciseSwapConfig,
): ExerciseSwapConfig => ({
  swapsEnabled: config.swapsEnabled,
  proposals: config.proposals.map(({ exercise }) => ({
    name: exercise.name,
    description: exercise.description,
    media: exercise.media,
    sets: toProposalSets(exercise.sets),
  })),
});

const unwrapSwapConfigs = (exercises: WorkoutExercise[]): unknown[] =>
  exercises.map(unwrapExerciseSwapConfig);

const unwrapExerciseSwapConfig = (exercise: WorkoutExercise): unknown => {
  switch (exercise.__typename) {
    case 'WorkoutExerciseSingle': {
      const { swapConfig, ...rest } = exercise;
      return swapConfig
        ? { ...rest, swapConfig: toFormSwapConfig(swapConfig) }
        : rest;
    }
    case 'WorkoutExerciseMultiple':
    case 'WorkoutExerciseAutoplayed':
      return {
        ...exercise,
        exercises: exercise.exercises?.map((child) =>
          child ? unwrapExerciseSwapConfig(child) : child,
        ),
      };
    default:
      return exercise;
  }
};

// Proposal media rides the REST upsert verbatim; strip __typename the same
// way the exercise's own media is stripped just below.
const stripProposalTypenames = (
  config: ExerciseSwapConfig,
): ExerciseSwapConfig => ({
  ...config,
  proposals: config.proposals.map((proposal) => ({
    ...proposal,
    media: proposal.media && omit(proposal.media, '__typename'),
  })),
});

const mapExerciseToPayload = (exercise: any, values: any, isClass: boolean) => {
  switch (exercise.type) {
    case ExerciseEnum.Type.Single.value:
      return {
        ...omit(exercise, 'id', 'duration', 'uuid', '__typename'),
        sets: exercise.sets.map(({ reps }: { reps: [string, string] }) => ({
          reps: [Number(reps[0]), reps[1]],
        })),
        media: exercise.media && omit(exercise.media, '__typename'),
        ...(exercise.swapConfig
          ? { swapConfig: stripProposalTypenames(exercise.swapConfig) }
          : {}),
      };
    case 11:
    case 10:
      return { ...omitObject(exercise, ['uuid', '__typename']) };
    case ExerciseEnum.Type.Multiple.value:
      return {
        type: exercise.type,
        ...omit(exercise, ['uuid', '__typename']),
        duration: exercise.duration
          ? [Number(exercise.duration[0]), exercise.duration[1]]
          : null,
        exercises: exercise.exercises.map(mapExerciseToPayload),
        media: exercise.media && omit(exercise.media, '__typename'),
      };
    case ExerciseEnum.Type.YouTube.value:
      return {
        ...omit(exercise, ['uuid', '__typename']),
        duration: exercise?.media?.duration
          ? [Number(exercise.media.duration), 'sec']
          : null,
        media:
          exercise.media &&
          omit(exercise.media, [
            '__typename',
            'creatorId',
            'aspectRatio',
            'createdAt',
            'description',
            'title',
            'type',
            'deletedAt',
          ]),
        name: isClass ? values.name : exercise.name,
        description: isClass ? values.description : exercise.description,
      };
    case 100:
      return runningTemplateToExercise(exercise);
  }
};

export const isAutoplayedItem = (
  item: WorkoutItem,
): item is LegacyHIIT | LegacyTabata => item.type === 10 || item.type === 11;

const withFreshChildIds = (e: LegacyExercise): LegacyExercise => {
  const id = uuid4();
  return { ...e, id, uuid: id };
};

export const withFreshIds = (item: WorkoutItem): WorkoutItem => {
  const id = uuid4();
  const children = (item as Circuit).exercises;
  return {
    ...item,
    // Templates have no id before their first save - don't invent one.
    ...(item.id === undefined ? {} : { id }),
    uuid: id,
    ...(children ? { exercises: children.map(withFreshChildIds) } : {}),
  };
};

export const exerciseTypeToString = (type: LegacyExercise['type']): string => {
  switch (type) {
    case ExerciseEnum.Type.Multiple.value:
      return 'Circuit';
    case ExerciseEnum.Type.YouTube.value:
      return 'Long Video';
    case 100:
      return 'Running';
    case ExerciseEnum.Type.Single.value:
    default:
      return 'Exercise';
  }
};

const emptyExercise: LegacyExercise = {
  uuid: uuid4(),
  name: '',
  id: '',
  type: ExerciseEnum.Type.Single.value,
  description: '',
  rest: undefined,
  sets: [
    {
      reps: [0, 'reps'],
    },
  ],
  media: undefined,
};

const emptyCircuit: Circuit = {
  uuid: uuid4(),
  id: '',
  name: '',
  rounds: 1,
  longRest: 60,
  type: ExerciseEnum.Type.Multiple.value as 3,
  description: '',
  rest: undefined,
  duration: [0, 'rounds'],
  exercises: [],
};

const emptyTabata = { ...emptyCircuit, type: 10 };
const emptyHIIT = { ...emptyCircuit, type: 11 };

const emptyVideo: Video = {
  uuid: uuid4(),
  name: '',
  type: ExerciseEnum.Type.YouTube.value,
  description: '',
  media: undefined,
};

export const emptyOutdoorSet = {
  uuid: uuid4(),
  time: 1,
  distance: 1,
  tempo: RunningTempo.Walk,
  audio: undefined,
  uploading: false,
};

const emptyRunningOutdoor: Running<RunningExerciseType.Outdoor> = {
  uuid: uuid4(),
  name: 'Running',
  measurementMode: 'time-based',
  type: 100,
  media: undefined,
  sets: [emptyOutdoorSet],
  variant: RunningExerciseType.Outdoor,
  uploading: false,
};

export const emptyTreadmillSet = {
  uuid: uuid4(),
  time: 1,
  speed: 0,
  incline: 0,
  audio: undefined,
  uploading: false,
};

export const initWorkout = (
  builderType: BuilderType,
  locationProgramId?: number,
) => ({
  name: '',
  description: '',
  tags: [],
  duration: undefined,
  messagePush: '',
  introVideo: undefined,
  thumbnail: undefined,
  programId: locationProgramId,
  freeSample: false,
  requiredEquipment: '',
  mediaExercise: undefined,
  exercises: builderType === 'class' ? [emptyVideo] : [],
  swapsEnabled: true,
});

export const initTemplates: Record<Template, object> = {
  exercise: emptyExercise,
  circuit: emptyCircuit,
  video: emptyVideo,
  running: emptyRunningOutdoor,
  HIIT: emptyHIIT,
  Tabata: emptyTabata,
};
